/* eslint-disable complexity */
import React from 'react';
import { noMobileInfusionZone } from '.';
import { HeroProduct, Product } from 'Client/components/Product';
import InfusionProduct from 'Client/components/Product/InfusionProduct';
import { LooksInfusion, ProductInterface } from 'Client/redux/products/types';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';

type FormattedProductProps = {
    looksInfusions?: LooksInfusion[] | null;
    product: ProductInterface;
    products?: ProductInterface[];
    desktopInfusionCounter?: React.MutableRefObject<number>;
    mobileInfusionCounter?: React.MutableRefObject<number>;
    isHero: boolean;
};

const inFusionProductsSpotsInGridMobile: number[] = [10, 24, 29, 38, 48, 59, 70, 85];
const insFusionProductsColumnMobile: string[] = [
    'left',
    'left',
    'right',
    'left',
    'left',
    'left',
    'center',
    'right',
    'right',
];

const insFusionProductsSpotsInGridDesktop: number[] = [19, 37, 49, 60, 80];
const insFusionProductsColumnDesktop: string[] = ['left', 'left', 'center', 'center', 'right'];

const infusionColors = [
    '#96897D',
    '#B2A490',
    '#BCB19D',
    '#BAAAA0',
    '#848484',
    '#96897D',
    '#B2A490',
    '#BCB19D',
    '#BAAAA0',
    '#848484',
];

const FormattedProduct: React.FC<FormattedProductProps> = (props): React.ReactElement => {
    const { looksInfusions, desktopInfusionCounter, mobileInfusionCounter, product, products, isHero } = props;
    const { p100PlpLooksInfusion } = useFeatureToggle();
    const { position } = product;

    if (p100PlpLooksInfusion && products && product.name !== '' && mobileInfusionCounter && desktopInfusionCounter) {
        const mobileCount = mobileInfusionCounter.current;
        const mobilePosition = inFusionProductsSpotsInGridMobile[mobileCount];

        const mobilePositionDiff = position - mobilePosition;
        /**
         * Mobile positioning
         * from the defined position consider
         * 2 previous row, 2 same row (including self)
         * and 2 next position.
         * Skip if it is in vicinity of hero position.
         */
        if (
            products &&
            mobilePosition &&
            mobilePositionDiff >= -2 &&
            mobilePositionDiff <= 3 &&
            !noMobileInfusionZone.includes(position)
        ) {
            const looksInfusionMobile = looksInfusions?.find(l => products[position].sku === l.relatedSku);
            if (looksInfusionMobile || mobilePositionDiff === 1) {
                mobileInfusionCounter.current += 1;
            }
            const columnPosition = insFusionProductsColumnMobile[mobileCount];
            const bottomColorMobile = infusionColors[mobileCount];

            return (
                <InfusionProduct
                    {...props}
                    key={position}
                    isInfusionMobile
                    columnPosition={columnPosition}
                    bottomColor={bottomColorMobile}
                    looksInfusion={looksInfusionMobile}
                />
            );
        }

        const desktopCount = desktopInfusionCounter.current;
        const desktopPosition = insFusionProductsSpotsInGridDesktop[desktopCount];

        /**
         * Desktop positioning
         * from the defined position consider
         * 3 previous row, 3 same row (including self)
         * Skip if it collides with hero position.
         */
        const desktopPositionDiff = position - desktopPosition;
        if (desktopPosition && products && !isHero && desktopPositionDiff >= -3 && desktopPositionDiff <= 2) {
            const columnPosition = insFusionProductsColumnDesktop[desktopCount];
            const bottomColorDesktop = infusionColors[desktopCount];
            const looksInfusionDesktop = looksInfusions?.find(
                (l: LooksInfusion) => products[position].sku === l.relatedSku
            );
            if (looksInfusionDesktop || desktopPositionDiff === 2) {
                desktopInfusionCounter.current += 1;
            }
            return (
                <InfusionProduct
                    {...props}
                    key={position}
                    isInfusionMobile={false}
                    columnPosition={columnPosition}
                    bottomColor={bottomColorDesktop}
                    looksInfusion={looksInfusionDesktop}
                />
            );
        }
    }

    if (isHero) {
        return <HeroProduct {...props} key={position} />;
    }

    return <Product {...props} key={position} />;
};

export default FormattedProduct;
